/**
 * Treatment × Area programmatic pages — Restore.Dental
 * URL pattern: /{treatment.slug}-{area.slug}  e.g. /invisalign-ealing, /dental-implants-chiswick
 * 5 treatments × 10 areas = 50 pages (general dentistry is covered by the /dentist-{area} pages).
 */
import { areas, getAreaBySlug } from './areas';

export const TREATMENT_AREA_SLUGS = ['invisalign', 'dental-implants', 'composite-bonding', 'emergency-dentist', 'cosmetic-dentist', 'iv-sedation'];

const a = (area) => area.name;
const pc = (area) => area.postcode;
const mins = (area) => `${area.distanceMinutes} minutes`;
const tube = (area) => area.tubes[0];

export const treatments = {
  invisalign: {
    slug: 'invisalign',
    name: 'Invisalign',
    noun: 'Invisalign clear aligners',
    hubPath: '/invisalign-acton',
    servicePath: '/services/invisalign',
    galleryTreatment: 'Invisalign',
    priceFrom: '£2,800',
    monthly: 'from £83/month at 0% APR',
    duration: '6–18 months',
    h1: (area) => `Invisalign in ${a(area)}`,
    title: (area) => `Invisalign ${a(area)} ${pc(area)} — From £2,800, 0% Finance | Restore`,
    description: (area) => `Invisalign clear aligners for ${a(area)} patients, ${mins(area)} away in Acton. 150+ cases by Dr Kartik Patel BDS. From £2,800, free 3D scan, 0% finance.`,
    bullets: ['From £2,800 · 0% finance', 'Free 3D scan & smile preview', '150+ cases by Dr Patel', 'Retainers included'],
    benefits: [
      'Virtually invisible — nobody at work or school needs to know',
      'Removable for eating, brushing and special occasions',
      'Digital planning shows your projected result before you commit',
      'Fewer and shorter appointments than fixed braces, with remote monitoring available',
      'Whitening and edge bonding can be included at the end for a complete finish',
    ],
    steps: [
      { title: 'Free consultation & 3D scan', body: 'A digital scan replaces messy impressions. We assess bite, gum health and suitability, then show you a simulation of your straightened smile.' },
      { title: 'Personalised ClinCheck plan', body: 'Dr Patel refines the tooth-by-tooth movement plan and confirms the number of aligners, timeline and fixed price.' },
      { title: 'Aligner fitting', body: 'Small tooth-coloured attachments are placed where needed. You wear each aligner set 20–22 hours a day, changing weekly.' },
      { title: 'Check-ups every 6–8 weeks', body: 'Short in-person or remote reviews to track progress. Refinement aligners are included if fine-tuning is needed.' },
      { title: 'Retainers & finishing', body: 'Fixed and/or removable retainers keep your result for life; optional whitening and bonding complete the transformation.' },
    ],
    prices: [
      ['Invisalign Lite (simple cases, up to 14 aligners)', 'from £2,800'],
      ['Invisalign Comprehensive', '£3,500 – £5,500'],
      ['Complex / extended treatment', 'up to £6,000'],
      ['Retainers (Vivera set)', 'included'],
      ['Free consultation & 3D scan', '£0'],
    ],
    faqs: [
      { question: 'How long does Invisalign take?', answer: 'Simple crowding or spacing usually takes 4–8 months; most comprehensive adult cases finish in 12–18 months. Wearing aligners 20–22 hours a day is the biggest factor in finishing on time.' },
      { question: 'Is Invisalign painful?', answer: 'You will feel pressure for a day or two after each new aligner, which is a sign the teeth are moving. It is far gentler than fixed braces and there are no wires or brackets to rub.' },
      { question: 'Do I have to wear retainers afterwards?', answer: 'Yes, and it is the step people skip. Teeth drift back towards their old position for life, so a set of Vivera retainers is included in our pricing and we usually bond a fine wire behind the lower front teeth as well. Without retention, a year of treatment unwinds in two or three.' },
    ],
    related: ['composite-bonding', 'cosmetic-dentist'],
  },

  'dental-implants': {
    slug: 'dental-implants',
    name: 'Dental Implants',
    noun: 'dental implants',
    hubPath: '/implants-acton',
    servicePath: '/services/dental-implants',
    galleryTreatment: 'Dental Implants',
    priceFrom: '£2,295',
    monthly: 'from £191/month at 0% APR',
    duration: '3–6 months',
    h1: (area) => `Dental Implants in ${a(area)}`,
    title: (area) => `Dental Implants ${a(area)} ${pc(area)} — From £2,295 | Restore`,
    description: (area) => `Dental implants for ${a(area)} patients at Restore.Dental Acton, ${mins(area)} away. CBCT-guided, single tooth to All-on-4. From £2,295, 0% finance. Dr Kartik Patel BDS.`,
    bullets: ['From £2,295 incl. crown', '3D CBCT-guided placement', 'Single tooth to All-on-4', '0% finance from £191/month'],
    benefits: [
      'Looks, feels and functions like a natural tooth',
      'Preserves jawbone that shrinks after tooth loss',
      'No drilling of healthy neighbouring teeth (unlike a bridge)',
      'Fixed alternative to dentures that slip or click',
      'With good care, implants routinely last 25+ years',
    ],
    steps: [
      { title: 'Assessment & 3D CBCT scan', body: 'We map bone volume, nerves and sinuses in 3D and give an honest opinion on whether an implant — or an alternative — is right for you.' },
      { title: 'Digital planning', body: 'The implant position, angle and depth are planned virtually and, where helpful, a surgical guide is printed for millimetre accuracy.' },
      { title: 'Implant placement', body: 'Placed under local anaesthetic (sedation available). Most patients say it is easier than an extraction and return to work the next day.' },
      { title: 'Healing (osseointegration)', body: 'Over 3–6 months the implant fuses with bone. A temporary tooth is provided where appearance matters.' },
      { title: 'Final crown or bridge', body: 'A custom ceramic restoration is fitted and colour-matched so nobody can tell which tooth is the implant.' },
    ],
    prices: [
      ['Single implant, abutment & crown', 'from £2,295'],
      ['Implant-retained bridge (3 teeth on 2 implants)', 'from £5,500'],
      ['All-on-4 full arch (per arch)', '£12,000 – £18,000'],
      ['Bone grafting (where needed)', 'from £450'],
      ['3D CBCT scan & planning', 'included'],
    ],
    faqs: [
      { question: 'Is implant surgery painful?', answer: 'The procedure is carried out under local anaesthetic and most patients describe it as easier than a tooth extraction. Mild soreness for two or three days is normal and controlled with over-the-counter painkillers. Sedation is available for anxious patients.' },
      { question: 'What if I have been told I don\'t have enough bone?', answer: 'Our CBCT scan gives a definitive answer. In many cases bone grafting (from £450) or a narrower implant makes treatment possible; where it does not, Dr Patel will say so and recommend the best alternative.' },
      { question: 'How long do dental implants last?', answer: 'With good hygiene and regular check-ups, the implant itself commonly lasts 25 years or a lifetime. The crown on top may need replacing after 10–15 years of normal wear.' },
    ],
    related: ['cosmetic-dentist', 'emergency-dentist'],
  },

  'composite-bonding': {
    slug: 'composite-bonding',
    name: 'Composite Bonding',
    noun: 'composite bonding',
    hubPath: '/composite-bonding-acton',
    servicePath: '/services/composite-veneers',
    galleryTreatment: 'Composite Bonding',
    priceFrom: '£250 per tooth',
    monthly: 'from £42/month at 0% APR',
    duration: 'one visit',
    h1: (area) => `Composite Bonding in ${a(area)}`,
    title: (area) => `Composite Bonding ${a(area)} ${pc(area)} — From £250/Tooth | Restore`,
    description: (area) => `Composite bonding for ${a(area)} patients at Restore.Dental Acton, ${mins(area)} away. Fix chips, gaps and uneven edges in one pain-free visit. From £250 per tooth.`,
    bullets: ['From £250 per tooth', 'Same-day, no drilling', 'Lasts 5–7 years', 'Free consultation'],
    benefits: [
      'Completed in a single visit — no temporaries, no waiting',
      'No drilling of healthy enamel, so it is fully reversible',
      'Pain-free; most cases need no injection',
      'Roughly a third of the price of porcelain veneers',
      'Perfect finishing touch after Invisalign or whitening',
    ],
    steps: [
      { title: 'Consultation & shade match', body: 'We discuss the shapes you want, take photographs and match composite shades to your natural teeth (or your freshly whitened shade).' },
      { title: 'Preparation', body: 'The tooth surface is cleaned and lightly conditioned — no drilling, no anaesthetic in most cases.' },
      { title: 'Layered sculpting', body: 'Dr Patel builds the tooth in thin layers of composite, recreating natural translucency and texture by hand.' },
      { title: 'Polish & bite check', body: 'The bonding is contoured, polished to a high gloss and checked against your bite so it feels natural immediately.' },
      { title: 'Aftercare & maintenance', body: 'Avoid biting hard objects and staining foods for 48 hours. A polish at each check-up keeps the bonding bright for years.' },
    ],
    prices: [
      ['Composite bonding (per tooth)', 'from £250'],
      ['Edge bonding — 4 front teeth', 'from £1,000'],
      ['Composite veneers (per tooth)', '£400 – £600'],
      ['Full composite smile makeover (8–10 teeth)', '£3,500 – £6,000'],
      ['Consultation', 'free'],
    ],
    faqs: [
      { question: 'How long does composite bonding last?', answer: 'Typically 5–7 years, longer with good care. Unlike veneers, it can be repaired or refreshed in a single visit rather than replaced.' },
      { question: 'Does composite bonding damage your teeth?', answer: 'No. Bonding is additive — composite resin is bonded to the natural tooth surface without removing enamel, so the treatment is reversible and your teeth are unchanged underneath.' },
      { question: 'Bonding or veneers — which is better?', answer: 'Bonding is ideal for chips, small gaps and edge reshaping and is far cheaper and reversible. Porcelain veneers last longer (10–15 years) and suit heavy discolouration or larger changes. We advise honestly which is right for your teeth.' },
    ],
    related: ['invisalign', 'cosmetic-dentist'],
  },

  'emergency-dentist': {
    slug: 'emergency-dentist',
    name: 'Emergency Dentist',
    noun: 'emergency dental care',
    hubPath: '/emergency-dentist-acton',
    servicePath: '/services/emergency-dentist',
    galleryTreatment: 'Emergency Repair',
    priceFrom: '£80 emergency exam',
    monthly: 'pay as you go',
    duration: 'same day',
    h1: (area) => `Emergency Dentist near ${a(area)}`,
    title: (area) => `Emergency Dentist ${a(area)} ${pc(area)} — Same-Day Appointments | Restore`,
    description: (area) => `Emergency dentist ${mins(area)} from ${a(area)}. Same-day appointments Mon–Sat for toothache, broken teeth, abscesses & lost crowns. Call 07787 814 064.`,
    bullets: ['Same-day slots Mon–Sat', 'Emergency exam £80–£120', 'Pain relief first, then a plan', 'Call/WhatsApp 07787 814 064'],
    benefits: [
      'Same-day appointments held every weekday and Saturday',
      'Pain relief at the first visit — we never just send you home with antibiotics',
      'Digital X-rays and honest diagnosis of the cause',
      'Broken teeth repaired or temporised on the spot',
      'Nervous patients welcome — sedation available for follow-up treatment',
    ],
    steps: [
      { title: 'Call or WhatsApp', body: 'Tell us what has happened. We will find the earliest slot — usually within hours — and advise how to manage pain until you arrive.' },
      { title: 'Emergency examination', body: 'A focused examination and X-ray of the problem area to find the cause: infection, fracture, exposed nerve or lost restoration.' },
      { title: 'Immediate relief', body: 'Draining an abscess, dressing a tooth, re-cementing a crown or starting root canal treatment — whatever stops the pain today.' },
      { title: 'Clear options', body: 'We explain the definitive fix, costs and timelines, and send a written summary to your WhatsApp.' },
      { title: 'Definitive treatment', body: 'Complete the repair at a follow-up visit, or move to a long-term plan for the teeth that caused the problem.' },
    ],
    prices: [
      ['Emergency examination & X-rays', '£80 – £120'],
      ['Temporary filling / crown re-cement', '£100 – £150'],
      ['Extraction', '£150 – £300'],
      ['Root canal (first stage)', 'from £250'],
      ['Out-of-hours advice via WhatsApp', 'free'],
    ],
    faqs: [
      { question: 'How much does an emergency dental appointment cost?', answer: 'The emergency examination with X-rays is £80–£120. Any treatment carried out on the day is quoted before we start — for example a temporary filling is £100–£150 and an extraction £150–£300.' },
      { question: 'What counts as a dental emergency?', answer: 'Severe or worsening toothache, facial swelling or an abscess, a knocked-out or broken tooth, bleeding that will not stop, a lost crown or filling causing pain, and trauma to the mouth. If in doubt, message us and we will advise.' },
      { question: 'My tooth has been knocked out — what should I do?', answer: 'Pick it up by the crown (not the root), rinse gently, and if possible place it back in the socket or keep it in milk. Come straight to us — re-implantation is most successful within 60 minutes.' },
    ],
    related: ['dental-implants', 'composite-bonding'],
  },

  'cosmetic-dentist': {
    slug: 'cosmetic-dentist',
    name: 'Cosmetic Dentist',
    noun: 'cosmetic dentistry',
    hubPath: '/cosmetic-dentist-acton',
    servicePath: '/services/digital-smile-design',
    galleryTreatment: 'Smile Makeover',
    priceFrom: '£250',
    monthly: 'from £83/month at 0% APR',
    duration: '1 visit to 6 months',
    h1: (area) => `Cosmetic Dentist in ${a(area)}`,
    title: (area) => `Cosmetic Dentist ${a(area)} ${pc(area)} — Veneers, Bonding, Whitening | Restore`,
    description: (area) => `Cosmetic dentist ${mins(area)} from ${a(area)}. Smile makeovers, veneers, bonding & whitening planned with Digital Smile Design by Dr Kartik Patel BDS. 0% finance.`,
    bullets: ['Digital Smile Design preview', 'Bonding, veneers, whitening, Invisalign', 'Natural, undetectable results', '0% finance over 3–60 months'],
    benefits: [
      'See a digital preview of your new smile before any treatment',
      'Conservative options first — bonding and whitening before veneers',
      'Hand-finished results designed to look natural, not "done"',
      'One clinician plans and delivers the whole case',
      'Whitening, alignment and reshaping combined into a single plan and price',
    ],
    steps: [
      { title: 'Smile consultation', body: 'We photograph and scan your smile and discuss what you like, what bothers you and the look you want.' },
      { title: 'Digital Smile Design', body: 'Your new smile is designed on screen, then converted to a trial "mock-up" you can wear and see in your own mouth.' },
      { title: 'Foundation first', body: 'Gum health, alignment (Invisalign) and whitening are addressed so cosmetic work sits on a healthy, bright base.' },
      { title: 'Bonding or veneers', body: 'Composite is sculpted directly, or porcelain veneers are crafted by our ceramist and fitted with minimal preparation.' },
      { title: 'Final review & protection', body: 'Bite is checked, edges polished and a night guard provided where needed to protect your investment.' },
    ],
    prices: [
      ['Composite bonding (per tooth)', 'from £250'],
      ['Composite veneers (per tooth)', '£400 – £600'],
      ['Porcelain veneers (per tooth)', '£800 – £1,200'],
      ['Professional whitening', '£350 – £850'],
      ['Full smile makeover (8–10 teeth)', '£3,500 – £12,000'],
    ],
    faqs: [
      { question: 'Will my teeth look fake?', answer: 'Not with us. We design shade, shape and texture to suit your face and age, and finish everything by hand. Most patients are told their smile looks "healthier", not "done".' },
      { question: 'Do I need veneers or is bonding enough?', answer: 'Bonding handles chips, small gaps and edge shaping beautifully at a fraction of the price. Veneers suit heavy discolouration, worn or misshapen teeth. We show you both options in the digital preview so you can decide.' },
      { question: 'Is cosmetic dentistry available on finance?', answer: 'Yes. All cosmetic treatment over £500 can be spread over 3–60 months at 0% APR through Tabeo, subject to status, with an instant online decision.' },
    ],
    related: ['composite-bonding', 'invisalign'],
  },

  'iv-sedation': {
    slug: 'iv-sedation',
    name: 'IV Sedation',
    noun: 'IV sedation dentistry',
    hubPath: '/iv-sedation-dentistry',
    servicePath: '/services/sedation',
    galleryTreatment: null,
    priceFrom: '£450',
    monthly: 'first hour, plus the treatment',
    duration: 'assessment, then one appointment',
    h1: (area) => `IV Sedation Dentist near ${a(area)}`,
    title: (area) => `IV Sedation Dentist ${a(area)} ${pc(area)} — From £450 | Restore`,
    description: (area) => `IV sedation dentist ${mins(area)} from ${a(area)}. Awake but deeply relaxed, usually little memory. From £450 plus treatment. Adult escort required.`,
    bullets: ['From £450 for the first hour', 'Awake and relaxed, usually little memory', 'Escort required — no driving that day', 'Assessment first, then a written quote'],
    benefits: [
      'Treatment that would take five or six visits can often be done in one appointment',
      'You stay awake and able to respond — this is not a general anaesthetic',
      'Most patients remember very little of the appointment itself',
      'Suits needle phobia, a strong gag reflex, or difficulty sitting still for long',
      'Given on site in Acton by Dr Kartik Patel BDS, with the team monitoring you throughout',
    ],
    steps: [
      { title: 'Sedation assessment', body: 'A shorter appointment first: we examine you, review your medical history and medication, explain what sedation will and will not do, and decide together whether it is the right option.' },
      { title: 'Plan and written quote', body: 'You leave knowing exactly what is being done, how long we expect to need and what the whole thing costs — sedation and treatment together. Then you decide in your own time.' },
      { title: 'Arrive with your escort', body: 'A responsible adult over 18 brings you or meets you here. We cannot sedate you if you arrive alone, so this is the part to arrange early.' },
      { title: 'Cannula, then treatment', body: 'A fine cannula goes into your hand or arm and the sedative follows, taking effect in a minute or two. You are monitored continuously while the treatment is carried out as planned.' },
      { title: 'Recovery and the rest of the day', body: 'Twenty to thirty minutes resting with us, then home with your escort. No driving, work or important decisions for the rest of the day. Most people feel normal again the next morning.' },
    ],
    prices: [
      ['Sedation assessment (in person)', 'from £75'],
      ['IV sedation — first hour', 'from £450'],
      ['Each additional hour', 'from £250'],
      ['The treatment itself', 'quoted separately'],
      ['0% finance on treatment over £500', '3–60 months'],
    ],
    faqs: [
      { question: 'Will I remember the treatment?', answer: 'Most people remember arriving, then very little until recovery. Some recall fragments and a few remember more than they expected, so we will not promise you a complete blank — only that the memory is usually hazy and far less distressing than being fully aware.' },
      { question: 'Do I definitely need someone to come with me?', answer: 'Yes. A responsible adult over 18 has to bring you or meet you here, take you home and ideally stay with you for the evening. We cannot go ahead if you arrive alone. If arranging an escort is difficult, tell us early and we will work around the timing.' },
      { question: 'Is IV sedation the same as being put to sleep?', answer: 'No. A general anaesthetic makes you fully unconscious and is given in hospital. IV sedation leaves you awake, breathing for yourself and able to respond, but deeply relaxed and uninterested in what is happening. It is sometimes called twilight sedation for that reason.' },
    ],
    related: ['dental-implants', 'emergency-dentist'],
  },
};

export const getTreatmentAreaCombos = () =>
  TREATMENT_AREA_SLUGS.flatMap((t) => areas.map((ar) => ({ path: `/${t}-${ar.slug}`, treatmentSlug: t, areaSlug: ar.slug })));

/** Resolve /invisalign-ealing → { treatment, area } */
export const resolveTreatmentArea = (pathname) => {
  const clean = pathname.replace(/^\//, '').replace(/\/$/, '');
  for (const t of TREATMENT_AREA_SLUGS) {
    if (clean.startsWith(`${t}-`)) {
      const area = getAreaBySlug(clean.slice(t.length + 1));
      if (area) return { treatment: treatments[t], area };
    }
  }
  return null;
};

export const nearestAreas = (area, n = 3) =>
  [...areas].filter((x) => x.slug !== area.slug).sort((x, y) => Math.abs(x.distanceMinutes - area.distanceMinutes) - Math.abs(y.distanceMinutes - area.distanceMinutes)).slice(0, n);
